<script lang="ts">
  /**
   * Главный холст: растровое изображение + SVG-оверлей с аннотациями.
   *
   * Обрабатывает мышь для рисования (все инструменты) и выделения/перемещения.
   * Система координат: всё в пикселях исходного изображения; вьюпорт
   * (scale, translate) применяется через transform на группе.
   */
  import { onMount } from 'svelte';
  import { editor } from '../../store/editor.svelte';
  import { hitTest, simplifyPath, shapeBBox } from '../../domain/geometry';
  import { createShape } from '../../domain/defaults';
  import { eventToImagePoint } from '../../utils/coord';
  import type { Point, Shape, ToolId } from '../../domain/types';
  import type { Snippet } from 'svelte';
  import ShapeView from './ShapeView.svelte';
  import MaskLayer from './MaskLayer.svelte';

  interface Props {
    empty?: Snippet;
  }
  let { empty }: Props = $props();

  let containerEl: HTMLDivElement | undefined = $state();

  // Состояние взаимодействия
  type DragState =
    | { kind: 'idle' }
    | { kind: 'drawing'; start: Point; shapeId: string; tool: ToolId }
    | { kind: 'arrowFree'; shapeId: string }
    | { kind: 'pencil'; shapeId: string }
    | { kind: 'moving'; shapeId: string; last: Point }
    | { kind: 'panning'; last: { x: number; y: number } }
    | { kind: 'resizing'; shapeId: string; startW: number; startH: number; startRadius: number; startPoint: Point };
  let drag = $state<DragState>({ kind: 'idle' });

  // Текстовый режим редактирования
  let editTextarea = $state<HTMLTextAreaElement | null>(null);

  // При входе в режим редактирования — сфокусировать textarea и выделить текст.
  $effect(() => {
    if (editor.editingTextId && editTextarea) {
      editTextarea.focus();
      editTextarea.select();
    }
  });

  onMount(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
      onWheel(e);
    };
    containerEl?.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      containerEl?.removeEventListener('wheel', handleWheel);
    };
  });

  function imgPoint(e: MouseEvent): Point {
    return eventToImagePoint(e, containerEl!, editor.project.viewport);
  }

  function nextZ(): number {
    return editor.project.shapes.reduce((m, s) => Math.max(m, s.z), -1) + 1;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Pointer
  // ─────────────────────────────────────────────────────────────────────────
  function onPointerDown(e: MouseEvent) {
    if (!containerEl) return;

    // Если сейчас редактируется текст — клик по холсту финиширует редактирование,
    // а дальше логика решит, начинать ли новое действие (текст/инструмент).
    if (editor.editingTextId) {
      commitTextEdit();
      // Если активен инструмент «текст» и клик был ЛКМ — сразу создаём новый текст.
      // (Текст-инструмент обрабатывается ниже.)
    }

    if (e.button === 1 || e.button === 2) {
      drag = { kind: 'panning', last: { x: e.clientX, y: e.clientY } };
      e.preventDefault();
      return;
    }
    if (e.button !== 0) return;

    const p = imgPoint(e);
    const tool = editor.activeTool;

    if (tool === 'select') {
      if (editor.selection.length > 0) {
        const selectedShape = editor.getShape(editor.selection[0]);
        if (selectedShape && (selectedShape.type === 'rect' || selectedShape.type === 'number')) {
          const bb = shapeBBox(selectedShape);
          const offset = selectedShape.type === 'rect' ? 0 : 4;
          const handleX = bb.x + bb.w + offset;
          const handleY = bb.y + bb.h + offset;
          
          const distToHandle = Math.hypot(p.x - handleX, p.y - handleY);
          const threshold = 12 / editor.project.viewport.scale;
          if (distToHandle <= threshold) {
            editor.commit();
            drag = {
              kind: 'resizing',
              shapeId: selectedShape.id,
              startW: selectedShape.type === 'rect' ? selectedShape.w : 0,
              startH: selectedShape.type === 'rect' ? selectedShape.h : 0,
              startRadius: selectedShape.type === 'number' ? selectedShape.radius : 0,
              startPoint: p,
            };
            e.preventDefault();
            return;
          }
        }
      }

      const sorted = [...editor.project.shapes].sort((a, b) => b.z - a.z);
      const hit = sorted.find((s) => s.visible && !s.locked && hitTest(s, p));
      if (hit) {
        editor.selectShape(hit.id, e.shiftKey || e.ctrlKey || e.metaKey);
        editor.commit();
        drag = { kind: 'moving', shapeId: hit.id, last: p };
      } else {
        editor.clearSelection();
        drag = { kind: 'panning', last: { x: e.clientX, y: e.clientY } };
      }
      return;
    }

    if (tool === 'text') {
      startText(p);
      return;
    }
    if (tool === 'number') {
      const id = startNumber(p);
      drag = { kind: 'drawing', start: p, shapeId: id, tool };
      return;
    }
    if (tool === 'pencil') {
      const id = startPencil(p);
      drag = { kind: 'pencil', shapeId: id };
      return;
    }


    // arrow: при зажатом Ctrl — свободная форма (змейка), иначе прямая
    if (tool === 'arrow' && e.ctrlKey) {
      const id = startArrowFree(p);
      drag = { kind: 'arrowFree', shapeId: id };
      return;
    }

    // arrow (прямая), rect, blur
    const id = startDragShape(tool, p);
    drag = { kind: 'drawing', start: p, shapeId: id, tool };
  }

  function onPointerMove(e: MouseEvent) {
    if (drag.kind === 'idle') return;
    const p = imgPoint(e);

    if (drag.kind === 'panning') {
      const dx = e.clientX - drag.last.x;
      const dy = e.clientY - drag.last.y;
      const vp = editor.project.viewport;
      editor.setViewport(vp.scale, vp.tx + dx, vp.ty + dy);
      drag = { ...drag, last: { x: e.clientX, y: e.clientY } };
      return;
    }
    if (drag.kind === 'resizing') {
      const dx = p.x - drag.startPoint.x;
      const dy = p.y - drag.startPoint.y;
      const s = editor.getShape(drag.shapeId);
      if (s) {
        if (s.type === 'rect') {
          editor.updateShape(s.id, {
            w: Math.max(10, drag.startW + dx),
            h: Math.max(10, drag.startH + dy)
          });
        } else if (s.type === 'number') {
          editor.updateShape(s.id, {
            radius: Math.max(12, Math.min(100, drag.startRadius + dx))
          });
        }
      }
      return;
    }
    if (drag.kind === 'moving') {
      const dx = p.x - drag.last.x;
      const dy = p.y - drag.last.y;
      editor.translateShape(drag.shapeId, dx, dy);
      drag = { ...drag, last: p };
      return;
    }
    if (drag.kind === 'drawing') {
      updateDragShape(drag.tool, drag.shapeId, drag.start, p);
      return;
    }
    if (drag.kind === 'arrowFree') {
      appendArrowFree(drag.shapeId, p);
      return;
    }
    if (drag.kind === 'pencil') {
      appendPencil(drag.shapeId, p);
      return;
    }
  }

  function onPointerUp(e: MouseEvent) {
    if (drag.kind === 'idle') return;

    if (drag.kind === 'drawing' && drag.tool === 'number') {
      finishNumber(drag.shapeId, drag.start, imgPoint(e));
    }
    if (drag.kind === 'drawing') {
      const s = editor.getShape(drag.shapeId);
      if (s && shapeIsEmpty(s)) {
        editor.deleteShape(drag.shapeId, false);
        editor.discardLastCommit();
      }
    }
    if (drag.kind === 'arrowFree') finalizeArrowFree(drag.shapeId);
    if (drag.kind === 'pencil') finalizePencil(drag.shapeId);

    drag = { kind: 'idle' };
  }

  function onDoubleClick(e: MouseEvent) {
    const p = imgPoint(e);
    const sorted = [...editor.project.shapes].sort((a, b) => b.z - a.z);
    const hit = sorted.find((s) => s.visible && s.type === 'text' && hitTest(s, p));
    if (hit) {
      editor.editingTextId = hit.id;
      editor.setTool('select');
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Создание/обновление shapes
  // ─────────────────────────────────────────────────────────────────────────
  function startDragShape(tool: ToolId, p: Point): string {
    editor.commit();
    const z = nextZ();
    const patch: Partial<Shape> =
      tool === 'arrow'
        ? { x: p.x, y: p.y, x2: p.x, y2: p.y }
        : tool === 'rect' || tool === 'blur'
          ? { x: p.x, y: p.y, w: 0, h: 0 }
          : {};
    const shape = createShape(tool, patch, z, editor.toolSettings[tool]);
    editor.addShape(shape, false);
    return shape.id;
  }

  function updateDragShape(tool: ToolId, id: string, start: Point, cur: Point) {
    if (tool === 'arrow') {
      editor.updateShape(id, { x2: cur.x, y2: cur.y });
    } else if (tool === 'rect' || tool === 'blur') {
      editor.updateShape(id, {
        x: Math.min(start.x, cur.x),
        y: Math.min(start.y, cur.y),
        w: Math.abs(cur.x - start.x),
        h: Math.abs(cur.y - start.y),
      });
    } else if (tool === 'number') {
      const s = editor.getShape(id);
      if (s && s.type === 'number') {
        const angle = (Math.atan2(cur.y - s.y, cur.x - s.x) * 180) / Math.PI;
        editor.updateShape(id, {
          arrowTarget: { x: cur.x, y: cur.y },
          arrowMode: 'target',
          arrowAngle: angle,
        });
      }
    }
  }

  function shapeIsEmpty(s: Shape): boolean {
    if (s.type === 'arrow') return Math.hypot(s.x2 - s.x, s.y2 - s.y) < 3;
    if (s.type === 'rect' || s.type === 'blur') return s.w < 3 || s.h < 3;
    if (s.type === 'pencil') return s.points.length < 2;
    return false;
  }

  // Arrow (свободная форма — змейка при зажатом Ctrl)
  function startArrowFree(p: Point): string {
    editor.commit();
    const shape = createShape(
      'arrow',
      { x: p.x, y: p.y, x2: p.x, y2: p.y, path: [{ ...p }] },
      nextZ(),
      editor.toolSettings.arrow,
    );
    editor.addShape(shape, false);
    return shape.id;
  }
  function appendArrowFree(id: string, p: Point) {
    const s = editor.getShape(id);
    if (s && s.type === 'arrow' && s.path) {
      const last = s.path[s.path.length - 1];
      // не добавляем точки, слишком близкие к предыдущей
      if (Math.hypot(p.x - last.x, p.y - last.y) >= 3) {
        const path = [...s.path, { ...p }];
        editor.replaceShape({ ...s, path, x2: p.x, y2: p.y });
      }
    }
  }
  function finalizeArrowFree(id: string) {
    const s = editor.getShape(id);
    if (s && s.type === 'arrow' && s.path && s.path.length >= 2) {
      // Сгладить путь (RDP + Catmull-Rom в arrowGeometry при рендере),
      // обновить концы по первой/последней точке.
      const simplified = simplifyPath(s.path, 2);
      if (simplified.length < 2) {
        editor.deleteShape(id, false);
        editor.discardLastCommit();
        return;
      }
      editor.replaceShape({
        ...s,
        path: simplified,
        x: simplified[0].x,
        y: simplified[0].y,
        x2: simplified[simplified.length - 1].x,
        y2: simplified[simplified.length - 1].y,
      });
    } else if (s && shapeIsEmpty(s)) {
      editor.deleteShape(id, false);
      editor.discardLastCommit();
    }
  }

  // Pencil
  function startPencil(p: Point): string {
    editor.commit();
    const shape = createShape('pencil', { points: [{ ...p }] }, nextZ(), editor.toolSettings.pencil);
    editor.addShape(shape, false);
    return shape.id;
  }
  function appendPencil(id: string, p: Point) {
    const s = editor.getShape(id);
    if (s && s.type === 'pencil') editor.replaceShape({ ...s, points: [...s.points, { ...p }] });
  }
  function finalizePencil(id: string) {
    const s = editor.getShape(id);
    if (s && s.type === 'pencil') {
      const simplified = simplifyPath(s.points, 1.5);
      const closed =
        simplified.length > 2 &&
        Math.hypot(simplified[0].x - simplified.at(-1)!.x, simplified[0].y - simplified.at(-1)!.y) < 12;
      if (simplified.length < 2) {
        editor.deleteShape(id, false);
        editor.discardLastCommit();
      } else {
        editor.replaceShape({ ...s, points: simplified, closed });
      }
    }
  }



  // Number
  function startNumber(p: Point): string {
    return editor.createNumberMarker(p, p);
  }
  function finishNumber(id: string, start: Point, end: Point) {
    const s = editor.getShape(id);
    if (!s || s.type !== 'number') return;
    const moved = Math.hypot(end.x - start.x, end.y - start.y);
    if (moved < 8) {
      editor.updateShape(id, { arrowMode: 'angle', arrowAngle: 0, arrowLength: 120 });
    }
  }

  // Text
  function startText(p: Point) {
    editor.commit();
    const shape = createShape('text', { x: p.x, y: p.y, text: 'Текст' }, nextZ(), editor.toolSettings.text);
    editor.addShape(shape, false);
    editor.editingTextId = shape.id;
    editor.selection = [shape.id];
    editor.setTool('select');
  }
  function commitTextEdit() {
    const s = editor.editingTextId ? editor.getShape(editor.editingTextId) : null;
    if (s && s.type === 'text' && s.text.trim() === '') editor.deleteShape(s.id);
    editor.editingTextId = null;
  }

  // Колесо мыши — зум с удержанием точки под курсором
  function onWheel(e: WheelEvent) {
    if (!containerEl) return;
    const vp = editor.project.viewport;
    const rect = containerEl.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    const newScale = Math.min(8, Math.max(0.1, vp.scale * factor));
    const imgX = (cx - vp.tx) / vp.scale;
    const imgY = (cy - vp.ty) / vp.scale;
    editor.setViewport(newScale, cx - imgX * newScale, cy - imgY * newScale);
  }

  const cursorClass = $derived.by(() => {
    if (drag.kind === 'panning') return 'grabbing';
    if (drag.kind === 'moving') return 'grabbing';
    if (editor.activeTool === 'select') return 'default';
    if (editor.activeTool === 'text') return 'text';
    return 'crosshair';
  });

  const vp = $derived(editor.project.viewport);
  const hasImage = $derived(!!editor.project.image);
  /** Видимые маски — нужны, чтобы условно монтировать MaskLayer. */
  const blurs = $derived(
    editor.shapesSorted.filter(
      (s) =>
        (s.type === 'blur' && s.visible && (s as import('../../domain/types').BlurShape).w > 0) ||
        (s.type === 'rect' && s.visible && s.fillEnabled && (s.fillMode === 'blur' || s.fillMode === 'pixelize') && (s as import('../../domain/types').RectShape).w > 0),
    ),
  );
</script>

<svelte:window onmousemove={onPointerMove} onmouseup={onPointerUp} />

<div bind:this={containerEl} class="relative w-full h-full overflow-hidden bg-canvas">
  {#if hasImage}
    <!-- Слой захвата событий мыши -->
    <div
      class="absolute inset-0"
      style={`cursor: ${cursorClass};`}
      onmousedown={onPointerDown}
      ondblclick={onDoubleClick}
      oncontextmenu={(e) => e.preventDefault()}
      role="presentation"
    >
      <!-- Обертка рабочего фрейма с отступами и фоном -->
      <div
        class="absolute select-none pointer-events-none"
        style={`
          left: ${vp.tx}px;
          top: ${vp.ty}px;
          transform-origin: 0 0;
          transform: scale(${vp.scale});
          width: ${editor.project.image?.width}px;
          height: ${editor.project.image?.height}px;
          ${editor.project.background.enabled ? `
            box-sizing: content-box;
            padding: ${editor.project.background.padding}px;
            background-color: ${editor.project.background.color};
            margin-left: -${editor.project.background.padding}px;
            margin-top: -${editor.project.background.padding}px;
          ` : ''}
        `}
      ></div>

      <!-- Растровое изображение -->
      <img
        src={editor.project.image?.src}
        alt="screenshot"
        class="absolute top-0 left-0 max-w-none select-none pointer-events-none"
        style={`
          transform: translate(${vp.tx}px, ${vp.ty}px) scale(${vp.scale});
          transform-origin: 0 0;
          ${editor.project.background.enabled ? `
            border-radius: ${editor.project.background.cornerRadius}px;
            box-shadow: ${editor.project.background.shadowEnabled ? '0 20px 40px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.06)' : 'none'};
          ` : ''}
        `}
        draggable="false"
      />

      <!-- Слой масок (blur/пикселизация): обработанные тайлы поверх изображения -->
      {#if editor.project.image}
        {#if blurs.length > 0}
          <MaskLayer
            src={editor.project.image.src}
            width={editor.project.image.width}
            height={editor.project.image.height}
            tx={vp.tx}
            ty={vp.ty}
            scale={vp.scale}
          />
        {/if}
      {/if}

      <!-- SVG-оверлей -->
      <svg class="absolute inset-0 w-full h-full pointer-events-none" style="overflow: visible;">
        <g transform={`translate(${vp.tx}, ${vp.ty}) scale(${vp.scale})`}>
          {#each editor.shapesSorted as shape (shape.id)}
            {#if shape.visible}
              <ShapeView {shape} selected={editor.selection.includes(shape.id)} />
            {/if}
          {/each}
        </g>
      </svg>

      <!-- Редактор текста -->
      {#if editor.editingTextId}
        {@const ts = editor.getShape(editor.editingTextId)}
        {#if ts && ts.type === 'text'}
          <div
            class="absolute"
            style={`left: ${vp.tx + ts.x * vp.scale}px; top: ${vp.ty + ts.y * vp.scale}px;`}
          >
            <textarea
              bind:this={editTextarea}
              class="bg-transparent outline outline-2 outline-blue-500 resize-none border-0"
              style={`font-size: ${ts.fontSize * vp.scale}px; font-family: ${ts.fontFamily}; color: ${ts.textColor}; line-height: 1.25; min-width: 40px;`}
              rows="2"
              value={ts.text}
              oninput={(e: Event & { currentTarget: HTMLTextAreaElement }) =>
                editor.updateShape(ts.id, { text: e.currentTarget.value })}
              onblur={commitTextEdit}
              onkeydown={(e: KeyboardEvent) => {
                // Enter (без Shift) — подтвердить и закрыть редактор.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  (e.currentTarget as HTMLTextAreaElement).blur();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  (e.currentTarget as HTMLTextAreaElement).blur();
                }
              }}
            ></textarea>
          </div>
        {/if}
      {/if}
    </div>
  {:else}
    {@render empty?.()}
  {/if}
</div>
